import React, { useContext, useState, useEffect } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  TextField,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { useSnackbar } from "../../contexts/InfoContext";
import { useAuth } from "../../contexts/AuthContext";
import useUserClaims from "../../hooks/useUserClaims";
import getInternationalDate from "../../utils/getDate";
import CustomTextField from "../Credit/component/CustomTextField";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import getRequiredUserData from "../../utils/getBranchInfo";
import CreateGivenAccessoriesToDeliveryGuys from "../../api/accessories_given_to_workers/create_given_accessories_to_deliveryGuy";
import { getAccessoryTypes } from "../../api/accessories_given_to_workers/accessoryTypes";
import fetchData from "../../api/services/Users/getUser";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  deliveryguyId: Yup.string().required("Please select a Delivery Guy"),
  accessoriesGiven: Yup.array()
    .of(
      Yup.object().shape({
        accessoryType: Yup.string().required("Accessory Type is required"),
        quantity: Yup.number()
          .required("Quantity is required")
          .positive("Quantity must be positive")
          .integer("Quantity must be an integer")
          .min(1, "Quantity must be at least 1"),
      })
    )
    .min(1, "At least one accessory is required"),
  notes: Yup.string().nullable(),
});

const GivenAccessoriesToDeliveryGuyForm = ({ source }) => {
  const params = useParams();
  const [showForm, setShowForm] = useState(false);
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();
  const userClaims = useUserClaims(user);
  const theme = useTheme();
  const userData = getRequiredUserData();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const [deliveryGuy, setDeliveryGuy] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const currentBranchId = params.id || userData.requiredId;

  useEffect(() => {
    const unsubscribe = fetchData("Deliveryturn", setDeliveryGuy);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (userData.requiredId) {
      const unsubscribe = getAccessoryTypes(
        userData.requiredId,
        setAccessories
      );
      return () => unsubscribe && unsubscribe();
    }
  }, [userData.requiredId]);

  const formik = useFormik({
    initialValues: {
      deliveryguyId: "",
      deliveryGuyName: "",
      accessoriesGiven: [],
      notes: "",
    },

    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const date = getInternationalDate();
        values.branchId = userClaims.finance
          ? user.uid
          : params.id
          ? params.id
          : user.displayName;

        if (!values.branchId) {
          handleCloseForm();
          throw {
            response: {
              data: {
                message:
                  "Branch information is not found. Please check your connection, refresh your browser, and try again.",
                type: "error",
              },
            },
          };
        }

        values.date = date;
        values.accessoriesGiven = values.accessoriesGiven.map((item) => ({
          ...item,
          quantity: parseInt(item.quantity, 10),
        }));

        const res = await CreateGivenAccessoriesToDeliveryGuys(user, values);
        openSnackbar(`${res.data.message} successfully created!`, "success");
        handleCloseForm();
      } catch (error) {
        if (error.response && error.response.data) {
          openSnackbar(
            error.response.data.message,
            error.response.data.type ? error.response.data.type : "error"
          );
        } else {
          openSnackbar(
            "An unexpected error occurred.Please kindly check your connection.",
            "error"
          );
        }
      }
      setIsSubmitting(false);
    },
  });

  const deliveryMan = deliveryGuy
    ? deliveryGuy[currentBranchId ? currentBranchId : ""]
    : [];
  const deliveryman = deliveryMan?.map((item) => [
    item.deliveryGuyName,
    item.deliveryManId,
  ]);

  const handleDeliveryGuyChange = (event) => {
    const Id = event.target.value;
    const Name = deliveryman.find((employee) => employee[1] === Id)?.[0] || "";
    formik.setFieldValue("deliveryGuyName", Name);
    formik.setFieldValue("deliveryguyId", Id);
  };

  const handleButtonClick = () => {
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    formik.resetForm();
  };

  const handleAddAccessoryItem = () => {
    formik.setFieldValue("accessoriesGiven", [
      ...formik.values.accessoriesGiven,
      {
        accessoryType: "",
        quantity: 1,
      },
    ]);
  };

  const handleReturnAccessoryItem = (index) => {
    const newAccessories = [...formik.values.accessoriesGiven];
    newAccessories.splice(index, 1);
    formik.setFieldValue("accessoriesGiven", newAccessories);
  };

  const handleAccessoryItemChange = (index, field, value) => {
    const newAccessories = [...formik.values.accessoriesGiven];
    newAccessories[index] = { ...newAccessories[index], [field]: value };
    formik.setFieldValue("accessoriesGiven", newAccessories);
  };

  return (
    <div>
      {userClaims.finance || userClaims.admin ? (
        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleButtonClick}
          >
            Add Accessory Given To Delivery Guy
          </Button>

          <Dialog
            open={showForm}
            onClose={handleCloseForm}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
              New Given Accessory to Delivery Guy
            </DialogTitle>
            <DialogContent
              sx={{ backgroundColor: theme.palette.background.alt }}
            >
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <FormControl
                      fullWidth
                      error={
                        formik.touched.deliveryguyId &&
                        Boolean(formik.errors.deliveryguyId)
                      }
                    >
                      <InputLabel
                        shrink
                        style={{
                          marginTop: "8px",
                          color: theme.palette.secondary[100],
                        }}
                      >
                        Delivery Guy
                      </InputLabel>
                      <Select
                        name="deliveryguyId"
                        value={formik.values.deliveryguyId}
                        onChange={handleDeliveryGuyChange}
                        onBlur={formik.handleBlur}
                        label="Delivery Guy"
                      >
                        <MenuItem value="">Select Delivery Guy</MenuItem>
                        {deliveryman?.map((branch) => (
                          <MenuItem key={branch[1]} value={branch[1]}>
                            {branch[0]}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.deliveryguyId &&
                        formik.errors.deliveryguyId && (
                          <Typography
                            variant="caption"
                            color="error"
                            style={{ marginTop: "4px" }}
                          >
                            {formik.errors.deliveryguyId}
                          </Typography>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <CustomTextField
                      name="notes"
                      label="Notes"
                      value={formik.values.notes}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      errors={formik.errors}
                      touched={formik.touched}
                      multiline
                      minRows={3}
                      maxRows={5}
                    />
                  </Grid>

                  {formik.values.accessoriesGiven.map((item, index) => (
                    <Grid item xs={12} key={index}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <TextField
                            select
                            name={`accessoriesGiven[${index}].accessoryType`}
                            label={`Accessory Type ${index + 1}`}
                            required
                            value={item.accessoryType}
                            onChange={(e) =>
                              handleAccessoryItemChange(
                                index,
                                "accessoryType",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            fullWidth
                            error={
                              formik.touched.accessoriesGiven?.[index]
                                ?.accessoryType &&
                              Boolean(
                                formik.errors.accessoriesGiven?.[index]
                                  ?.accessoryType
                              )
                            }
                            helperText={
                              formik.touched.accessoriesGiven?.[index]
                                ?.accessoryType &&
                              formik.errors.accessoriesGiven?.[index]
                                ?.accessoryType
                            }
                            InputLabelProps={{
                              shrink: true,
                              style: {
                                marginTop: "8px",
                                color: theme.palette.secondary[100],
                              },
                            }}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  style: {
                                    backgroundColor:
                                      theme.palette.background.alt,
                                    color: "inherit",
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="">Select Accessory</MenuItem>
                            {accessories.map((accessory) => (
                              <MenuItem
                                key={accessory.id}
                                value={accessory.type}
                              >
                                {accessory.type}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name={`accessoriesGiven[${index}].quantity`}
                            label="Quantity"
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              handleAccessoryItemChange(
                                index,
                                "quantity",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.accessoriesGiven?.[index]
                                ?.quantity &&
                              Boolean(
                                formik.errors.accessoriesGiven?.[index]
                                  ?.quantity
                              )
                            }
                            helperText={
                              formik.touched.accessoriesGiven?.[index]
                                ?.quantity &&
                              formik.errors.accessoriesGiven?.[index]?.quantity
                            }
                            fullWidth
                            InputLabelProps={{
                              shrink: true,
                              style: { color: theme.palette.secondary[100] },
                            }}
                            inputProps={{ min: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Button
                            variant="contained"
                            color="error"
                            fullWidth
                            onClick={() => handleReturnAccessoryItem(index)}
                          >
                            Return
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleAddAccessoryItem}
                    >
                      Add Accessory
                    </Button>
                    {typeof formik.errors.accessoriesGiven === "string" && (
                      <Typography
                        variant="caption"
                        color="error"
                        style={{ marginTop: "4px", display: "block" }}
                      >
                        {formik.errors.accessoriesGiven}
                      </Typography>
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    alignItems="end"
                    justifyContent="end"
                  >
                    <DialogActions
                      sx={{ backgroundColor: theme.palette.background.alt }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleCloseForm}
                        color="secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={formik.isSubmitting} // Disable while submitting
                      >
                        Submit
                      </Button>
                    </DialogActions>
                  </Grid>
                </Grid>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default GivenAccessoriesToDeliveryGuyForm;
